import { useState } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import StaticBlob from '../utils/StaticBlob';

function ConfirmEmailPendingScreen({ apiUrl, email, onBackToLogin }) {
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const handleResend = async () => {
    if (Date.now() < cooldownUntil) return;
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetchWithRetry(`${apiUrl}/api/auth/resend-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resend');
      setStatus('sent');
      setCooldownUntil(Date.now() + 30000);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  const cooldownActive = Date.now() < cooldownUntil;

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)] flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-500">
      <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden">
        <StaticBlob color="var(--color-accent-warm)" tintAlpha={0.35} className="absolute top-20 left-10 w-72 h-72" />
        <StaticBlob color="var(--color-ink-light)" tintAlpha={0.35} className="absolute bottom-20 right-10 w-96 h-96" />
      </div>

      <div
        className="glass-morphism rounded-3xl shadow-2xl p-10 w-full max-w-md relative z-10 bg-[var(--color-paper-warm)] border border-[var(--color-ink-subtle)]/10 animate-slideUp"
        style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
      >
        <div className="text-center mb-8">
          <div className="inline-flex p-4 rounded-3xl mb-6">
            <img
              src={process.env.PUBLIC_URL + '/android-chrome-192x192.png'}
              alt="Steadily.me logo"
              className="w-16 h-16 opacity-90 contrast-125 filter sepia-[.25]"
            />
          </div>
          <h1 className="text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-3">
            Check your inbox
          </h1>
          <Mail className="w-12 h-12 mx-auto text-[var(--color-accent-warm)] my-4" />
          <p className="text-[var(--color-ink-light)] text-base font-serif">
            We sent a confirmation link to
          </p>
          <p className="text-[var(--color-ink-main)] font-semibold mt-1 break-all">{email}</p>
          <p className="text-[var(--color-ink-light)] text-sm font-serif italic mt-4">
            Click the link in that email to activate your account. The link and the 6-character code in it both expire in 2 hours.
          </p>
        </div>

        {status === 'sent' && (
          <div className="flex items-start space-x-2 text-green-700 text-sm bg-green-500/10 border border-green-500/20 rounded-xl p-3 mb-4">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>Sent! Check your inbox again.</span>
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-start space-x-2 text-red-600 text-sm bg-red-50/50 border-l-4 border-red-500 p-3 rounded-lg font-medium mb-4">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{errorMsg}</span>
          </div>
        )}

        <button
          onClick={handleResend}
          disabled={status === 'sending' || cooldownActive}
          className="w-full py-4 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] font-bold font-serif text-lg hover:bg-[var(--color-ink-light)] transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
        >
          {status === 'sending'
            ? 'Sending...'
            : cooldownActive
              ? 'Resend (wait 30s)'
              : 'Resend confirmation email'}
        </button>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onBackToLogin}
            className="text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors font-medium font-serif italic border-b border-transparent hover:border-[var(--color-ink-main)]"
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmEmailPendingScreen;
